import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTrailNavigate } from '../../context/EntityTrailContext';

/**
 * Drop-in replacement for react-router's `<Link>` on detail -> detail
 * navigation: a plain left-click carries the breadcrumb trail along via
 * router state (see EntityTrailContext). The trail is resolved at click
 * time (inside the handler), not at render time — the origin a
 * `DetailBreadcrumb` registers is only available once its effect has run,
 * which can be after this link's own first render, so reading it during
 * render would risk a stale/empty trail on first paint.
 *
 * `to` stays a real href throughout (via `<Link>`), so middle-click,
 * ctrl/cmd/shift-click, and a custom `onClick` that calls
 * `preventDefault()` all keep their normal browser/router behavior.
 */
function TrailLink({ to, onClick, children, ...rest }) {
	const navigateWithTrail = useTrailNavigate();

	const handleClick = (event) => {
		onClick?.(event);
		if (event.defaultPrevented) {
			return;
		}
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}
		event.preventDefault();
		navigateWithTrail(to);
	};

	return (
		<Link to={to} onClick={handleClick} {...rest}>
			{children}
		</Link>
	);
}

TrailLink.propTypes = {
	to: PropTypes.string.isRequired,
	onClick: PropTypes.func,
	children: PropTypes.node,
};

export default TrailLink;
